import { apiClient as api } from './apiClient';
import type { CatalogosAltas } from './altasCasosApi';

// ═══════════════════════════════════════════════════════════════
// Grid de clientes con alta directa (F1.2, GESCL-T04) — contrato
// del backend (ClientesGridController). No modificar sin coordinar.
// ═══════════════════════════════════════════════════════════════

/** Qué subconjunto de la base muestra el grid (D-GC-56). Coincide con `@ID_TIPO_REGISTRO` del SP. */
export enum TipoRegistroGrid {
  Todos = 0,
  /** Código distinto de PR…: migrados de GDeco y validados por F1.4. */
  Definitivos = 1,
  /** Provisionales PR…: altas web aún sin validar (viven en el listado de gestión). */
  Provisionales = 2,
  /** Con fecha de baja (las bajas cambian estado, nunca borran — A6). */
  DeBaja = 3,
}

/**
 * Clasificación comercial del cliente (catálogo `GC_C_TIPO_CLIENTE`, D-GC-57).
 * Ojo: NO es la "procedencia del registro" que insinuaba el mockup — eso es TipoRegistroGrid.
 */
export enum ClasificacionCliente {
  SinDeterminar = 0,
  Normal = 1,
  AtencionPreferente = 2,
  Controversia = 3,
  Competencia = 4,
}

export const CLASIFICACION_LABELS: Record<number, string> = {
  [ClasificacionCliente.SinDeterminar]: 'Sin determinar',
  [ClasificacionCliente.Normal]: 'Normal',
  [ClasificacionCliente.AtencionPreferente]: 'Atención preferente',
  [ClasificacionCliente.Controversia]: 'Controversia',
  [ClasificacionCliente.Competencia]: 'Competencia',
};

export interface ClienteGridQuery {
  /**
   * Búsqueda libre por nombre completo, código o email. El backend la resuelve en dos pasadas:
   * prefijo primero (instantáneo, es la única forma de que el índice del nombre haga seek) y
   * "contiene" solo si eso no encuentra nada. Teclear "nombre apellido" va rápido; un apellido
   * suelto cuesta ~1,5 s.
   */
  busqueda?: string;
  /**
   * Caja "ID o código". Un número busca el ID Gescom exacto **o** el código CL{n}/PR{n}: en los
   * clientes migrados el código no se corresponde con el ID (CL21345 es el cliente 72962). Un
   * texto que empieza por letras busca el código por prefijo.
   */
  idCodigo?: string;
  idProvincia?: number;
  /** undefined = todos · true = con alta de ficha de compra vigente · false = sin ella. */
  inversor?: boolean;
  /** undefined = todos · true = titular vigente de al menos una farmacia · false = no titular. */
  titularFarmacia?: boolean;
  /** Filtro de 2º nivel. Clasificación comercial (`GC_C_TIPO_CLIENTE`, D-GC-57). */
  idTipoCliente?: number;
  /** Filtro de 2º nivel. Contiene, entre TODOS los teléfonos del cliente (no solo el principal). */
  telefono?: string;
  /** Filtro de 2º nivel. Prefijo (los códigos postales son jerárquicos). */
  codigoPostal?: string;
  /** Filtro de 2º nivel. Contiene. */
  poblacion?: string;
  /**
   * Filtro de 2º nivel. false (defecto): los clientes de baja NO se listan, salvo que
   * `tipoRegistro` sea `DeBaja` (ahí la intención ya es inequívoca). true: se listan mezcladas
   * con el resto — el FE las resalta.
   */
  incluirBajas?: boolean;
  tipoRegistro?: TipoRegistroGrid;
  pagina?: number;
  tamanio?: number;
}

export interface ClienteGridFila {
  idCliente: number;
  codigo?: string | null;
  provisional: boolean;
  idTratamiento?: number | null;
  nombre?: string | null;
  apellido1?: string | null;
  apellido2?: string | null;
  email?: string | null;
  telefono?: string | null;
  idProvincia?: number | null;
  provincia?: string | null;
  inversor: boolean;
  /** Titular vigente de al menos una farmacia. */
  titularFarmacia: boolean;
  idTipoCliente?: number | null;
  tipoCliente?: string | null;
  /** Nula en los clientes migrados sin fecha de alta. */
  fechaAlta?: string | null;
  /** No nula = cliente de baja. */
  fechaBaja?: string | null;
}

export interface ClienteGridPagina {
  clientes: ClienteGridFila[];
  /** Total de la base sin filtros: el denominador de "X de Y". */
  totalBase: number;
  totalFiltrado: number;
  pagina: number;
  tamanio: number;
  /** 1 = búsqueda resuelta por prefijo (rápida) · 2 = ha hecho falta "contiene" (lenta). */
  modoBusqueda: number;
}

/** Formulario "Crear cliente" (alta directa, spec §5.2). */
export interface AltaDirectaRequest {
  idTratamiento?: number | null;
  nombre: string;
  apellido1?: string;
  apellido2?: string;
  anioNacimiento?: number | null;
  email?: string;
  prefijoMovil?: string;
  movil?: string;
  idProvincia?: number | null;
  codigoPostal?: string;
  idPais?: number | null;
  inversor: boolean;
  idPlazoCompra?: number | null;
  boletin: boolean;
  tramosFacturacion: number[];
  provinciasInteres: number[];
  notasProcedencia?: string;
}

export interface AltaDirectaResultado {
  idCliente: number;
  codigoCl: string;
  /** Caso nacido en estado "Alta directa": el rastro en el listado de gestión. */
  idCaso: number;
  fichaCompraCreada: boolean;
  geolocalizacionResuelta: boolean;
}

/**
 * Consulta del grid. POST (no GET): los criterios son PII (nombre, email, código) y no deben
 * viajar en la query string ni acabar en logs de acceso — mismo criterio que el popup de
 * coincidentes. No muta estado.
 */
export const consultarClientes = async (query: ClienteGridQuery): Promise<ClienteGridPagina> => {
  const res = await api.post('/api/clientes/consulta', query);
  return res.data;
};

/**
 * Catálogos de los selects del grid y del alta directa. Mismos que los del tratamiento, pero
 * bajo el recurso del grid: un usuario de Sistemas puede tener `Clientes.Grid.Read` sin tener
 * `Clientes.Altas.Read`.
 */
export const leerCatalogosGrid = async (): Promise<CatalogosAltas> => {
  const res = await api.get('/api/clientes/catalogos');
  return res.data;
};

/** Alta directa: crea el cliente con CL definitivo + su caso-rastro. NO es idempotente (R-GC-08). */
export const crearClienteDirecto = async (req: AltaDirectaRequest): Promise<AltaDirectaResultado> => {
  const res = await api.post('/api/clientes', req);
  return res.data;
};
